/**
 * Super Console — platform-operator cockpit spanning every tenant.
 *
 * Sections (top → bottom):
 *   1. Platform KPI strip      · loads /api/super/platform-kpis
 *   2. Toolbar (search / plan / status filter + New Agency)
 *   3. Agency table            · one row per tenant, click to open detail drawer
 *   4. Agency detail drawer    · loads /api/super/agencies/{id} — usage, admins,
 *                                 audit trail, impersonation, plan / status flip
 *
 * Only reachable by role="super_admin" (route gated in App.js).
 */
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import api, { errorMessage, startImpersonation } from "../lib/api";
import { PageHeader, ActionButton, GhostButton, PillBadge, FormField, inputClass } from "../components/Primitives";
import { Modal } from "../components/Modal";
import {
  Plus, Pause, Play, Trash2, Globe, Search, DollarSign, Users, Building2,
  TrendingUp, TrendingDown, X, LogIn, Shield, HardDrive, History, Activity,
  Radio, Ghost, Moon, ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const PLANS = [
  { value: "free", label: "Free · 10g · 5s · 5u", short: "Free", price: 0 },
  { value: "pro", label: "Pro · 100g · 50s · 50u", short: "Pro", price: 199 },
  { value: "scale", label: "Scale · 1kg · 500s · 500u", short: "Scale", price: 999 },
];

const emptyAgency = { name: "", plan: "free", admin_name: "", admin_email: "", admin_password: "" };

function fmtUSD(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(v >= 10_000 ? 0 : 1)}k`;
  return `$${v.toLocaleString()}`;
}

function fmtBytes(n) {
  const v = Number(n) || 0;
  if (v >= 1e9) return `${(v / 1e9).toFixed(2)} GB`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)} MB`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)} KB`;
  return `${v} B`;
}

function fmtRelative(iso) {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(0, (now - then) / 1000);
  if (s < 60) return `${Math.round(s)}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.round(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

/* ────────────────────────────────────────────────────────────────
 * KPI strip — top-of-console platform snapshot
 * ────────────────────────────────────────────────────────────────*/
function KpiTile({ label, value, sub, tone = "default", icon: Icon, testId }) {
  const toneCls = {
    default: "border-[#27272A] text-white",
    green: "border-[#34C759]/30 text-[#34C759]",
    red: "border-[#FF3B30]/30 text-[#FF3B30]",
    amber: "border-[#FF9F0A]/30 text-[#FF9F0A]",
    blue: "border-[#0A84FF]/30 text-[#0A84FF]",
  }[tone] || "border-[#27272A] text-white";
  return (
    <div
      data-testid={testId}
      className={`border ${toneCls.split(" ")[0]} bg-[#0A0A0A] p-4 min-w-0`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="label-mute truncate">{label}</div>
        {Icon && <Icon className={`size-3.5 shrink-0 ${toneCls.split(" ").slice(1).join(" ")}`} />}
      </div>
      <div className={`font-display text-3xl font-black tracking-tight mt-1 ${toneCls.split(" ").slice(1).join(" ")}`}>{value}</div>
      {sub && <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1 truncate">{sub}</div>}
    </div>
  );
}

function KpiStrip({ kpis }) {
  if (!kpis) {
    return <div className="grid grid-cols-2 lg:grid-cols-6 gap-3" data-testid="super-kpi-strip-loading">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="border border-[#27272A] bg-[#0A0A0A] p-4 h-24 animate-pulse" />
      ))}
    </div>;
  }

  // Sparkline for weekly signups (simple bars, no external lib).
  const maxWk = Math.max(1, ...(kpis.signups_weekly || []).map((w) => w.count));
  const spark = (kpis.signups_weekly || []).map((w) => (
    <div
      key={w.week_start}
      className="flex-1 bg-[#0A84FF]/40 hover:bg-[#0A84FF]"
      style={{ height: `${8 + Math.round((w.count / maxWk) * 24)}px` }}
      title={`${w.week_start}: ${w.count}`}
    />
  ));

  return (
    <div className="grid grid-cols-2 lg:grid-cols-6 gap-3" data-testid="super-kpi-strip">
      <KpiTile
        testId="kpi-agencies"
        label="Agencies"
        value={kpis.total_agencies}
        sub={`${kpis.active_agencies} active · ${kpis.suspended_agencies} suspended`}
        icon={Building2}
      />
      <KpiTile
        testId="kpi-mrr"
        label="Total MRR"
        value={fmtUSD(kpis.mrr_total)}
        sub={`pro ${kpis.agencies_by_plan?.pro || 0} · scale ${kpis.agencies_by_plan?.scale || 0}`}
        tone="green"
        icon={DollarSign}
      />
      <KpiTile
        testId="kpi-guards"
        label="Officers Under Management"
        value={kpis.total_guards}
        sub={`${kpis.total_sites} sites · ${kpis.total_users} users`}
        icon={Shield}
      />
      <KpiTile
        testId="kpi-active-shifts"
        label="Active Shifts Right Now"
        value={kpis.active_shifts_today}
        sub={`${kpis.total_clients} clients platform-wide`}
        tone="blue"
        icon={Activity}
      />
      <div
        data-testid="kpi-signups"
        className="border border-[#27272A] bg-[#0A0A0A] p-4"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="label-mute truncate">Signups (8w)</div>
          <TrendingUp className="size-3.5 text-[#0A84FF]" />
        </div>
        <div className="font-display text-3xl font-black tracking-tight mt-1">
          {kpis.signups_weekly?.reduce((s, w) => s + w.count, 0) || 0}
        </div>
        <div className="flex items-end gap-0.5 mt-2 h-8">{spark}</div>
      </div>
      <KpiTile
        testId="kpi-churn"
        label="Churn (30d)"
        value={kpis.churn_30d}
        sub="agencies suspended · last 30 days"
        tone={kpis.churn_30d > 0 ? "amber" : "default"}
        icon={TrendingDown}
      />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
 * Live-session strip — real-time seat activity across every tenant
 * ────────────────────────────────────────────────────────────────*/
function LiveSessionsStrip() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [nextRefreshAt, setNextRefreshAt] = useState(null);
  const [now, setNow] = useState(Date.now());
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/super/live-sessions?window_seconds=300");
      setData(r.data);
      setNextRefreshAt(Date.now() + 15_000);
    } catch (err) {
      // Silent — the strip is best-effort and shouldn't yank a toast on
      // every 15-second poll if the network hiccups.
      if (process.env.NODE_ENV === "development") console.debug("live-sessions poll skipped:", err?.message);
    }
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 15_000);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      clearInterval(timerRef.current);
      clearInterval(tick);
    };
  }, [load]);

  const secondsUntilRefresh = useMemo(
    () => Math.max(0, Math.round(((nextRefreshAt || 0) - now) / 1000)),
    [nextRefreshAt, now],
  );

  if (!data) {
    return (
      <div className="border border-[#27272A] bg-[#0A0A0A] p-4 h-20 animate-pulse" data-testid="live-sessions-loading" />
    );
  }

  const t = data.totals || {};
  const hasImpersonated = t.impersonated_sessions > 0;
  const hasOffHours = t.off_hours_admin_sessions > 0;

  return (
    <div className="border border-[#27272A] bg-[#0A0A0A]" data-testid="live-sessions-strip">
      {/* Header */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-4 px-4 py-3 hover:bg-[#0E0E0E] text-left"
        data-testid="live-sessions-toggle"
      >
        <div className="flex items-center gap-2 shrink-0">
          <span className="relative flex size-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#34C759] opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full size-2.5 bg-[#34C759]" />
          </span>
          <span className="label-mute">Live sessions</span>
        </div>
        <div className="flex items-center gap-x-6 gap-y-2 text-sm flex-1 flex-wrap">
          <div className="flex items-center gap-2 min-w-[110px]" data-testid="live-users-count">
            <Users className="size-4 text-zinc-500 shrink-0" />
            <span className="font-mono font-bold text-white text-xl leading-none tabular-nums">{t.users_live || 0}</span>
            <span className="text-zinc-500 text-[10px] uppercase tracking-widest whitespace-nowrap">signed in</span>
          </div>
          <div className="flex items-center gap-2 min-w-[100px]" data-testid="live-agencies-count">
            <Building2 className="size-4 text-zinc-500 shrink-0" />
            <span className="font-mono font-bold text-white text-xl leading-none tabular-nums">{t.agencies_live || 0}</span>
            <span className="text-zinc-500 text-[10px] uppercase tracking-widest whitespace-nowrap">tenants</span>
          </div>
          {hasImpersonated && (
            <div className="flex items-center gap-2 min-w-[95px]" data-testid="live-impersonated-count">
              <Ghost className="size-4 text-[#FF9F0A] shrink-0" />
              <span className="font-mono font-bold text-[#FF9F0A] text-xl leading-none tabular-nums">{t.impersonated_sessions}</span>
              <span className="text-[#FF9F0A]/80 text-[10px] uppercase tracking-widest whitespace-nowrap">shadow</span>
            </div>
          )}
          {hasOffHours && (
            <div className="flex items-center gap-2 min-w-[115px]" data-testid="live-offhours-count">
              <Moon className="size-4 text-[#0A84FF] shrink-0" />
              <span className="font-mono font-bold text-[#0A84FF] text-xl leading-none tabular-nums">{t.off_hours_admin_sessions}</span>
              <span className="text-[#0A84FF]/80 text-[10px] uppercase tracking-widest whitespace-nowrap">off-hours</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0 text-xs font-mono text-zinc-500">
          <Radio className="size-3" />
          <span data-testid="live-refresh-in">refresh in {secondsUntilRefresh}s</span>
          {expanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
        </div>
      </button>

      {/* Expanded — grouped by tenant */}
      {expanded && (
        <div className="border-t border-[#27272A] max-h-[420px] overflow-y-auto" data-testid="live-sessions-expanded">
          {(data.agencies || []).length === 0 ? (
            <div className="p-8 text-center text-zinc-500 text-sm">
              Nobody is signed in right now.
            </div>
          ) : (
            <ul className="divide-y divide-[#27272A]">
              {data.agencies.map((a) => (
                <li key={a.agency_id || "__unassigned__"} className="px-4 py-3" data-testid={`live-agency-${a.agency_id || "unassigned"}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="font-display font-black uppercase tracking-tight text-sm truncate">{a.agency_name}</div>
                    <span className="label-mute">{a.agency_slug || "—"}</span>
                    <div className="ml-auto flex items-center gap-2 text-xs">
                      <PillBadge tone="green">{a.count} live</PillBadge>
                      {a.impersonated_count > 0 && (
                        <PillBadge tone="amber">
                          <Ghost className="size-3 inline mr-1" />
                          {a.impersonated_count} shadow
                        </PillBadge>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1.5">
                    {a.active_users.map((u) => (
                      <div
                        key={u.id}
                        className="flex items-center gap-2 text-xs"
                        data-testid={`live-user-${u.id}`}
                      >
                        <span className="relative flex size-1.5 shrink-0">
                          <span className="absolute inline-flex h-full w-full rounded-full bg-[#34C759] opacity-75 animate-ping" />
                          <span className="relative inline-flex rounded-full size-1.5 bg-[#34C759]" />
                        </span>
                        <span className="text-zinc-200 font-mono truncate max-w-[180px]" title={u.email}>
                          {u.name}
                        </span>
                        <span className="text-zinc-500 text-[10px] uppercase tracking-widest">{u.role}</span>
                        {u.impersonated_by_email && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 border border-[#FF9F0A]/40 text-[#FF9F0A] uppercase tracking-widest flex items-center gap-1"
                            title={`Impersonated by ${u.impersonated_by_email}`}
                          >
                            <Ghost className="size-2.5" /> shadow
                          </span>
                        )}
                        {u.is_off_hours && (
                          <span className="text-[10px] px-1.5 py-0.5 border border-[#0A84FF]/40 text-[#0A84FF] uppercase tracking-widest flex items-center gap-1">
                            <Moon className="size-2.5" /> off-hours
                          </span>
                        )}
                        <span className="ml-auto text-zinc-500 shrink-0">{fmtRelative(u.last_active_at)}</span>
                      </div>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────
 * Agency detail drawer
 * ────────────────────────────────────────────────────────────────*/
function AgencyDetailDrawer({ agencyId, onClose, onMutated }) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!agencyId) return;
    setLoading(true);
    try {
      const r = await api.get(`/super/agencies/${agencyId}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(errorMessage(e, "Detail failed"));
      onClose();
    } finally {
      setLoading(false);
    }
  }, [agencyId, onClose]);
  useEffect(() => { load(); }, [load]);

  if (!agencyId) return null;

  const toggleStatus = async () => {
    const next = detail.status === "active" ? "suspended" : "active";
    setBusy(true);
    try {
      await api.patch(`/super/agencies/${agencyId}`, { status: next });
      toast.success(`${detail.name} → ${next}`);
      await load();
      onMutated?.();
    } catch (e) { toast.error(errorMessage(e, "Status flip failed")); }
    finally { setBusy(false); }
  };

  const changePlan = async (plan) => {
    setBusy(true);
    try {
      await api.patch(`/super/agencies/${agencyId}`, { plan });
      toast.success(`${detail.name} → ${plan}`);
      await load();
      onMutated?.();
    } catch (e) { toast.error(errorMessage(e, "Plan change failed")); }
    finally { setBusy(false); }
  };

  const purge = async () => {
    if (!window.confirm(`PERMANENTLY delete "${detail.name}" and ALL its data across every collection? This CANNOT be undone.`)) return;
    if (!window.confirm(`Second confirmation. Type-your-mind moment. Delete ${detail.name}? This will remove ${(detail.usage?.guards || 0)} guards, ${(detail.usage?.sites || 0)} sites, ${(detail.usage?.users || 0)} users.`)) return;
    setBusy(true);
    try {
      await api.delete(`/super/agencies/${agencyId}`);
      toast.success(`Purged ${detail.name}`);
      onMutated?.();
      onClose();
    } catch (e) { toast.error(errorMessage(e, "Delete failed")); }
    finally { setBusy(false); }
  };

  const impersonate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/super/agencies/${agencyId}/impersonate-admin`);
      startImpersonation(r.data.token, {
        name: r.data.target?.name,
        email: r.data.target?.email,
        role: r.data.target?.role,
        agency_name: r.data.agency?.name,
      });
      toast.success(`Shadow session started as ${r.data.target?.email}`);
      // Send them to the tenant's default landing — dashboard.
      navigate("/");
    } catch (e) { toast.error(errorMessage(e, "Impersonation failed")); }
    finally { setBusy(false); }
  };

  return (
    <Modal
      onClose={onClose}
      title={loading ? "Loading agency…" : detail?.name}
      testId="agency-detail-drawer"
      wide
    >
      {loading || !detail ? (
        <div className="text-zinc-500 text-sm p-6">Loading…</div>
      ) : (
        <div className="space-y-5">
          {/* Header ribbon */}
          <div className="flex flex-wrap items-center gap-3 border border-[#27272A] bg-[#0A0A0A] p-4">
            <div className="flex-1 min-w-[200px]">
              <div className="label-mute">{detail.slug}</div>
              <div className="font-display text-2xl font-black uppercase tracking-tight mt-0.5">{detail.name}</div>
              <div className="flex items-center gap-2 mt-2 text-xs">
                <PillBadge tone={detail.status === "active" ? "green" : "amber"}>{detail.status}</PillBadge>
                <span className="text-zinc-400">
                  Created {new Date(detail.created_at).toLocaleDateString()} · Last admin sign-in {fmtRelative(detail.last_admin_login_at)}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <ActionButton onClick={impersonate} icon={LogIn} disabled={busy} testid="agency-impersonate-btn">
                Log in as admin
              </ActionButton>
              <GhostButton onClick={toggleStatus} icon={detail.status === "active" ? Pause : Play} disabled={busy} testid="agency-drawer-toggle">
                {detail.status === "active" ? "Suspend" : "Resume"}
              </GhostButton>
              <button
                onClick={purge}
                disabled={busy}
                data-testid="agency-drawer-delete"
                className="border border-[#27272A] hover:border-[#FF3B30] hover:text-[#FF3B30] text-zinc-400 px-3 py-2 flex items-center gap-2 text-xs uppercase tracking-widest transition-colors disabled:opacity-40"
              >
                <Trash2 className="size-3.5" /> Hard delete
              </button>
            </div>
          </div>

          {/* Billing + plan */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="border border-[#27272A] bg-[#0A0A0A] p-4">
              <div className="label-mute">Contract MRR</div>
              <div className="font-display text-2xl font-black text-[#34C759] mt-1">{fmtUSD(detail.mrr)}</div>
              <div className="mt-3">
                <div className="label-mute mb-1">Plan</div>
                <select
                  data-testid="agency-drawer-plan"
                  className={inputClass}
                  value={detail.plan || "free"}
                  onChange={(e) => changePlan(e.target.value)}
                  disabled={busy}
                >
                  {PLANS.map((p) => <option key={p.value} value={p.value}>{p.label} · ${p.price}/mo</option>)}
                </select>
              </div>
            </div>
            <div className="border border-[#27272A] bg-[#0A0A0A] p-4">
              <div className="label-mute flex items-center gap-1.5"><HardDrive className="size-3" /> Storage</div>
              <div className="font-display text-2xl font-black mt-1">{fmtBytes(detail.storage_bytes)}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-3">Officer documents + photo attachments</div>
            </div>
            <div className="border border-[#27272A] bg-[#0A0A0A] p-4">
              <div className="label-mute">Plan limits</div>
              <div className="mt-2 space-y-1 text-xs font-mono">
                <div>Officers: <span className="text-white">{detail.usage?.guards ?? 0}</span> / {detail.limits?.max_guards ?? "∞"}</div>
                <div>Sites: <span className="text-white">{detail.usage?.sites ?? 0}</span> / {detail.limits?.max_sites ?? "∞"}</div>
                <div>Users: <span className="text-white">{detail.usage?.users ?? 0}</span> / {detail.limits?.max_users ?? "∞"}</div>
              </div>
            </div>
          </div>

          {/* Usage detail */}
          <div className="border border-[#27272A] bg-[#0A0A0A] p-4" data-testid="agency-drawer-usage">
            <div className="label-mute mb-3 flex items-center gap-1.5"><Activity className="size-3" /> Usage</div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              {[
                ["Officers", detail.usage?.guards],
                ["Sites", detail.usage?.sites],
                ["Users", detail.usage?.users],
                ["Clients", detail.usage?.clients],
                ["Shifts · 30d", detail.usage?.shifts_30d],
                ["Incidents · 30d", detail.usage?.incidents_30d],
              ].map(([label, val]) => (
                <div key={label}>
                  <div className="label-mute">{label}</div>
                  <div className="font-mono font-bold text-lg mt-0.5">{val ?? 0}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Admins */}
          <div className="border border-[#27272A] bg-[#0A0A0A]" data-testid="agency-drawer-admins">
            <div className="px-4 py-3 border-b border-[#27272A] label-mute flex items-center gap-1.5">
              <Users className="size-3" /> Admins & operators ({detail.admins?.length || 0})
            </div>
            {(detail.admins || []).length === 0 ? (
              <div className="p-4 text-xs text-zinc-500 italic">No elevated users on this tenant.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left label-mute border-b border-[#27272A]">
                  <tr>
                    <th className="px-4 py-2">Name</th>
                    <th className="px-4 py-2">Email</th>
                    <th className="px-4 py-2">Role</th>
                    <th className="px-4 py-2">Last sign-in</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#27272A]">
                  {detail.admins.map((u) => (
                    <tr key={u.id} className="hover:bg-[#0E0E0E]">
                      <td className="px-4 py-2">{u.name || "—"}</td>
                      <td className="px-4 py-2 font-mono text-zinc-300">{u.email}</td>
                      <td className="px-4 py-2"><PillBadge tone="default">{u.role}</PillBadge></td>
                      <td className="px-4 py-2 text-zinc-400">{fmtRelative(u.last_login_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Audit trail */}
          <div className="border border-[#27272A] bg-[#0A0A0A]" data-testid="agency-drawer-audit">
            <div className="px-4 py-3 border-b border-[#27272A] label-mute flex items-center gap-1.5">
              <History className="size-3" /> Recent activity ({detail.audit_events?.length || 0} events)
            </div>
            {(detail.audit_events || []).length === 0 ? (
              <div className="p-4 text-xs text-zinc-500 italic">No audited actions yet.</div>
            ) : (
              <ul className="divide-y divide-[#27272A]">
                {detail.audit_events.map((ev) => (
                  <li key={ev.id} className="px-4 py-3 flex items-start gap-3 text-sm hover:bg-[#0E0E0E]" data-testid={`audit-row-${ev.id}`}>
                    <div className="w-40 shrink-0 text-xs font-mono text-zinc-500">{fmtRelative(ev.created_at)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <PillBadge tone={ev.action === "delete" ? "red" : ev.action === "impersonate" ? "amber" : "default"}>
                          {ev.action}
                        </PillBadge>
                        <span className="text-zinc-400 text-xs">{ev.entity_type}{ev.entity_id ? ` · ${String(ev.entity_id).slice(0, 8)}` : ""}</span>
                        <span className="text-zinc-500 text-xs">·</span>
                        <span className="text-zinc-300 text-xs font-mono truncate">{ev.user_email || "system"}</span>
                      </div>
                      {ev.summary && <div className="text-xs text-zinc-400 mt-0.5 break-words">{ev.summary}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ────────────────────────────────────────────────────────────────
 * SuperConsole page
 * ────────────────────────────────────────────────────────────────*/
export default function SuperConsole() {
  const [agencies, setAgencies] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyAgency);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("q", search.trim());
      if (planFilter) params.set("plan", planFilter);
      if (statusFilter) params.set("status", statusFilter);
      const [r, k] = await Promise.all([
        api.get(`/super/agencies${params.toString() ? "?" + params.toString() : ""}`),
        api.get("/super/platform-kpis"),
      ]);
      setAgencies(r.data || []);
      setKpis(k.data || null);
    } catch (e) {
      toast.error(errorMessage(e, "Could not load Super Console"));
    } finally {
      setLoading(false);
    }
  }, [search, planFilter, statusFilter]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/super/agencies", form);
      toast.success(`Created ${form.name}`);
      setOpen(false); setForm(emptyAgency); load();
    } catch (e) {
      toast.error(errorMessage(e, "Create failed"));
    } finally {
      setBusy(false);
    }
  };

  const filterChipCount = useMemo(
    () => [search.trim(), planFilter, statusFilter].filter(Boolean).length,
    [search, planFilter, statusFilter],
  );

  return (
    <div>
      <PageHeader
        eyebrow="Platform"
        title="Super Console"
        subtitle="Every agency on this Iris Security deployment. Cross-tenant — be careful."
        icon={Globe}
        action={<ActionButton onClick={() => setOpen(true)} icon={Plus} testid="new-agency-btn">New Agency</ActionButton>}
      />

      <div className="p-8 space-y-6">
        <KpiStrip kpis={kpis} />

        <LiveSessionsStrip />

        {/* Toolbar — search + filters */}
        <div className="flex flex-wrap items-center gap-3" data-testid="super-toolbar">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="size-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="super-search"
              placeholder="Search by name or slug…"
              className={`${inputClass} pl-9`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            data-testid="super-plan-filter"
            className={`${inputClass} max-w-[160px]`}
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
          >
            <option value="">All plans</option>
            {PLANS.map((p) => <option key={p.value} value={p.value}>{p.short}</option>)}
          </select>
          <select
            data-testid="super-status-filter"
            className={`${inputClass} max-w-[160px]`}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
          {filterChipCount > 0 && (
            <button
              data-testid="super-clear-filters"
              onClick={() => { setSearch(""); setPlanFilter(""); setStatusFilter(""); }}
              className="text-xs uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30] flex items-center gap-1"
            >
              <X className="size-3" /> Clear ({filterChipCount})
            </button>
          )}
          <div className="ml-auto text-xs font-mono text-zinc-500">
            {agencies.length} agenc{agencies.length === 1 ? "y" : "ies"}
          </div>
        </div>

        {/* Agencies table */}
        <div data-testid="super-agencies-list">
          {loading ? (
            <div className="text-zinc-500 text-sm">Loading…</div>
          ) : agencies.length === 0 ? (
            <div className="border border-[#27272A] p-12 text-center text-zinc-500 scanlines">
              No agencies match those filters.
            </div>
          ) : (
            <div className="border border-[#27272A] divide-y divide-[#27272A]">
              {agencies.map((a) => (
                <button
                  key={a.id}
                  onClick={() => setSelectedId(a.id)}
                  data-testid={`agency-row-${a.id}`}
                  className="w-full grid grid-cols-12 gap-4 px-5 py-4 items-center hover:bg-[#0E0E0E] text-left transition-colors"
                >
                  <div className="col-span-3 min-w-0">
                    <div className="font-display font-black uppercase tracking-tight text-base truncate">{a.name}</div>
                    <div className="label-mute mt-0.5 truncate">{a.slug}</div>
                  </div>
                  <div className="col-span-1">
                    <PillBadge tone="default">{(a.plan || "free").toUpperCase()}</PillBadge>
                  </div>
                  <div className="col-span-1 font-mono text-sm text-[#34C759]">
                    {fmtUSD(a.mrr)}
                  </div>
                  <div className="col-span-1">
                    <PillBadge tone={a.status === "active" ? "green" : "amber"}>{a.status}</PillBadge>
                  </div>
                  <div className="col-span-4 grid grid-cols-3 gap-3 text-xs text-zinc-400">
                    <div><span className="label-mute">Officers</span><div className="text-zinc-200 mt-0.5 font-mono">{a.usage?.guards ?? 0}</div></div>
                    <div><span className="label-mute">Sites</span><div className="text-zinc-200 mt-0.5 font-mono">{a.usage?.sites ?? 0}</div></div>
                    <div><span className="label-mute">Users</span><div className="text-zinc-200 mt-0.5 font-mono">{a.usage?.users ?? 0}</div></div>
                  </div>
                  <div className="col-span-2 text-right text-xs text-zinc-500">
                    Last login {fmtRelative(a.last_admin_login_at)}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* New Agency modal (unchanged) */}
      {open && (
      <Modal onClose={() => setOpen(false)} title="Provision new agency" testId="new-agency-modal">
        <form onSubmit={create} className="space-y-4">
          <FormField label="Agency name">
            <input className={inputClass} value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="new-agency-name" />
          </FormField>
          <FormField label="Plan">
            <select className={inputClass} value={form.plan} onChange={(e) => set("plan", e.target.value)} data-testid="new-agency-plan">
              {PLANS.map((p) => <option key={p.value} value={p.value}>{p.label} · ${p.price}/mo</option>)}
            </select>
          </FormField>
          <FormField label="First admin — full name">
            <input className={inputClass} value={form.admin_name} onChange={(e) => set("admin_name", e.target.value)} required data-testid="new-agency-admin-name" />
          </FormField>
          <FormField label="First admin — email">
            <input className={inputClass} type="email" value={form.admin_email} onChange={(e) => set("admin_email", e.target.value)} required data-testid="new-agency-admin-email" />
          </FormField>
          <FormField label="First admin — password (8+)">
            <input className={inputClass} type="password" value={form.admin_password} onChange={(e) => set("admin_password", e.target.value)} required data-testid="new-agency-admin-password" />
          </FormField>
          <div className="pt-2 flex items-center gap-3">
            <ActionButton type="submit" disabled={busy} icon={Plus} testid="new-agency-submit">
              {busy ? "Creating…" : "Create agency"}
            </ActionButton>
            <GhostButton onClick={() => setOpen(false)} type="button" testid="new-agency-cancel">Cancel</GhostButton>
          </div>
        </form>
      </Modal>
      )}

      {/* Detail drawer */}
      <AgencyDetailDrawer
        agencyId={selectedId}
        onClose={() => setSelectedId(null)}
        onMutated={load}
      />
    </div>
  );
}
